import React, { useState, useEffect } from "react";
import hosys from "@/api/api";
import { useVenue } from "@/components/shared/VenueContext";
import PageHeader from "@/components/shared/PageHeader";
import QuickLoader from "@/components/shared/QuickLoader";
import EmptyState from "@/components/shared/EmptyState";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Settings, Save, Loader2, Clock, Thermometer, SprayCan,
  AlertTriangle, FileText, Bell, Plus, Trash2
} from "lucide-react";
import { Badge } from "@/components/ui/badge";

const TYPE_LABELS = {
  temperature: { label: "Temperatures", icon: Thermometer, color: "text-blue-600", bg: "bg-blue-50" },
  cleaning: { label: "Neteja", icon: SprayCan, color: "text-emerald-600", bg: "bg-emerald-50" },
  incident: { label: "Incidències", icon: AlertTriangle, color: "text-amber-600", bg: "bg-amber-50" },
  document_expiry: { label: "Documents", icon: FileText, color: "text-purple-600", bg: "bg-purple-50" },
  time_tracking: { label: "Fitxatges", icon: Clock, color: "text-gray-600", bg: "bg-gray-50" },
};

const DEFAULT_SETTINGS = {
  temp_check_times: ["09:00", "14:00", "19:00"],
  cleaning_deadline: "23:00",
  doc_expiry_warn_days: 30,
  doc_expiry_critical_days: 7,
  incident_escalate_days: 3,
  enabled_types: ["temperature", "cleaning", "incident", "document_expiry", "time_tracking"],
  min_priority_notify: "medium",
};

export default function AlertSettings() {
  const { currentVenue, userRole, loading: venueLoading } = useVenue();
  const [settings, setSettings] = useState(null);
  const [settingsId, setSettingsId] = useState(null);
  const [form, setForm] = useState(DEFAULT_SETTINGS);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [newCheckTime, setNewCheckTime] = useState("");

  useEffect(() => {
    if (currentVenue) loadSettings();
  }, [currentVenue]);

  const loadSettings = async () => {
    const items = await hosys.entities.AlertSettings.filter({ venue_id: currentVenue.id });
    if (items.length > 0) {
      setSettings(items[0]);
      setSettingsId(items[0].id);
      setForm({ ...DEFAULT_SETTINGS, ...items[0] });
    } else {
      setForm({ ...DEFAULT_SETTINGS });
    }
  };

  const handleSave = async () => {
    setSaving(true);
    const data = { ...form, venue_id: currentVenue.id };
    if (settingsId) {
      await hosys.entities.AlertSettings.update(settingsId, data);
    } else {
      const created = await hosys.entities.AlertSettings.create(data);
      setSettingsId(created.id);
    }
    // Audit log
    await hosys.entities.AuditLog.create({
      venue_id: currentVenue.id,
      action: "settings_changed",
      entity_type: "AlertSettings",
      entity_id: settingsId || "new",
      description: "Configuració d'alertes actualitzada",
    });
    setSaving(false);
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  const toggleType = (type) => {
    const enabled = form.enabled_types || [];
    if (enabled.includes(type)) {
      setForm(f => ({ ...f, enabled_types: enabled.filter(t => t !== type) }));
    } else {
      setForm(f => ({ ...f, enabled_types: [...enabled, type] }));
    }
  };

  const addCheckTime = () => {
    if (!newCheckTime || form.temp_check_times?.includes(newCheckTime)) return;
    const times = [...(form.temp_check_times || []), newCheckTime].sort();
    setForm(f => ({ ...f, temp_check_times: times }));
    setNewCheckTime("");
  };

  const removeCheckTime = (t) => {
    setForm(f => ({ ...f, temp_check_times: (f.temp_check_times || []).filter(x => x !== t) }));
  };

  if (venueLoading) return <QuickLoader />;
  if (!currentVenue) return <EmptyState icon={Settings} title="Cap local" description="Selecciona un local primer." />;

  const isEditable = userRole === "superadmin" || userRole === "admin";

  return (
    <div className="space-y-6">
      <PageHeader
        title="Configuració d'Alertes"
        subtitle={currentVenue.name}
        actions={isEditable && (
          <Button onClick={handleSave} disabled={saving} className="bg-[#7B2D3F] hover:bg-[#5B1D2F] gap-2">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {saved ? "Guardat ✓" : "Guardar"}
          </Button>
        )}
      />

      {!isEditable && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-sm text-amber-700">
          Necessites rol d'administrador per canviar la configuració.
        </div>
      )}

      {/* Types enabled */}
      <Card className="p-5 border-0 shadow-sm">
        <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <Bell className="w-4 h-4 text-[#7B2D3F]" /> Tipus d'alertes actives
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {Object.entries(TYPE_LABELS).map(([key, cfg]) => {
            const Icon = cfg.icon;
            const isEnabled = (form.enabled_types || []).includes(key);
            return (
              <div key={key} className={`flex items-center justify-between p-3 rounded-xl border transition-all ${isEnabled ? `${cfg.bg} border-transparent` : "bg-gray-50 border-gray-200"}`}>
                <div className="flex items-center gap-2">
                  <Icon className={`w-4 h-4 ${isEnabled ? cfg.color : "text-gray-400"}`} />
                  <span className={`text-sm font-medium ${isEnabled ? "text-gray-900" : "text-gray-400"}`}>{cfg.label}</span>
                </div>
                <Switch checked={isEnabled} onCheckedChange={() => isEditable && toggleType(key)} disabled={!isEditable} />
              </div>
            );
          })}
        </div>
      </Card>

      {/* Temperature check times */}
      <Card className="p-5 border-0 shadow-sm">
        <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <Thermometer className="w-4 h-4 text-blue-600" /> Franges horaries de control de temperatures
        </h3>
        <p className="text-sm text-gray-500 mb-3">S'avisarà si no hi ha registres passat 30 min d'aquestes hores.</p>
        <div className="flex flex-wrap gap-2 mb-3">
          {(form.temp_check_times || []).map(t => (
            <div key={t} className="flex items-center gap-1 bg-blue-100 text-blue-800 rounded-lg px-3 py-1.5 text-sm font-semibold">
              <Clock className="w-3 h-3" /> {t}
              {isEditable && (
                <button onClick={() => removeCheckTime(t)} className="ml-1 hover:text-blue-900">
                  <Trash2 className="w-3 h-3" />
                </button>
              )}
            </div>
          ))}
        </div>
        {isEditable && (
          <div className="flex gap-2 max-w-xs">
            <Input type="time" value={newCheckTime} onChange={e => setNewCheckTime(e.target.value)} className="text-sm" />
            <Button size="sm" variant="outline" onClick={addCheckTime} className="gap-1">
              <Plus className="w-3 h-3" /> Afegir
            </Button>
          </div>
        )}
      </Card>

      {/* Cleaning deadline */}
      <Card className="p-5 border-0 shadow-sm">
        <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <SprayCan className="w-4 h-4 text-emerald-600" /> Hora límit per neteges
        </h3>
        <div className="max-w-xs">
          <Label className="text-sm text-gray-600 mb-1 block">Hora límit per completar les neteges diàries</Label>
          <Input
            type="time"
            value={form.cleaning_deadline || "23:00"}
            onChange={e => setForm(f => ({ ...f, cleaning_deadline: e.target.value }))}
            disabled={!isEditable}
            className="max-w-[140px]"
          />
          <p className="text-xs text-gray-400 mt-1">Si hi ha neteges pendents a partir d'aquesta hora, es genera un avís.</p>
        </div>
      </Card>

      {/* Documents */}
      <Card className="p-5 border-0 shadow-sm">
        <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <FileText className="w-4 h-4 text-purple-600" /> Avisos de caducitat documental
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-md">
          <div>
            <Label className="text-sm text-gray-600 mb-1 block">Dies d'antelació (avís)</Label>
            <Input
              type="number" min={1} max={365}
              value={form.doc_expiry_warn_days ?? 30}
              onChange={e => setForm(f => ({ ...f, doc_expiry_warn_days: parseInt(e.target.value) }))}
              disabled={!isEditable}
            />
            <p className="text-xs text-gray-400 mt-0.5">Avís de prioritat mitjana</p>
          </div>
          <div>
            <Label className="text-sm text-gray-600 mb-1 block">Dies d'antelació (crític)</Label>
            <Input
              type="number" min={1} max={60}
              value={form.doc_expiry_critical_days ?? 7}
              onChange={e => setForm(f => ({ ...f, doc_expiry_critical_days: parseInt(e.target.value) }))}
              disabled={!isEditable}
            />
            <p className="text-xs text-gray-400 mt-0.5">Avís de prioritat alta/crítica</p>
          </div>
        </div>
      </Card>

      {/* Incidents */}
      <Card className="p-5 border-0 shadow-sm">
        <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 text-amber-600" /> Escalada d'incidències
        </h3>
        <div className="max-w-xs">
          <Label className="text-sm text-gray-600 mb-1 block">Dies fins escalar incidència greu</Label>
          <Input
            type="number" min={1} max={30}
            value={form.incident_escalate_days ?? 3}
            onChange={e => setForm(f => ({ ...f, incident_escalate_days: parseInt(e.target.value) }))}
            disabled={!isEditable}
          />
          <p className="text-xs text-gray-400 mt-1">Si una incidència alta/crítica porta més d'aquests dies oberta, es genera alerta crítica.</p>
        </div>
      </Card>

      {/* Min priority */}
      <Card className="p-5 border-0 shadow-sm">
        <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <Bell className="w-4 h-4 text-[#7B2D3F]" /> Prioritat mínima per notificació
        </h3>
        <div className="max-w-xs">
          <Label className="text-sm text-gray-600 mb-1 block">Mostrar alertes a partir de:</Label>
          <Select value={form.min_priority_notify || "medium"} onValueChange={v => setForm(f => ({ ...f, min_priority_notify: v }))} disabled={!isEditable}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="low">Baixa (totes)</SelectItem>
              <SelectItem value="medium">Mitjana i superior</SelectItem>
              <SelectItem value="high">Alta i crítica</SelectItem>
              <SelectItem value="critical">Només crítiques</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </Card>
    </div>
  );
}